/**
 * Time conversion utilities for UTC and local time calculations
 */

/**
 * Converts UTC time to local time using UTC offset
 * @param utcTime - Time in 4-digit format (e.g., "0715" for 07:15)
 * @param utcOffset - Offset in format "+0800" or "-0500"
 * @param dayChange - Current day change value (0, 1, -1, etc.)
 * @returns Object with local time string and new day change value
 */
export function utcToLocalTime(
  utcTime: string,
  utcOffset: string,
  dayChange: number = 0
): { localTime: string; dayChange: number } | null {
  // Validate inputs
  if (!utcTime || utcTime.length !== 4 || !/^\d{4}$/.test(utcTime)) {
    return null;
  }

  // Validate offset format (both "+0800" and "+08:00" are accepted)
  if (!utcOffset || !/^[+-]\d{2}:?\d{2}$/.test(utcOffset)) {
    return null;
  }

  // Parse UTC time
  const utcHours = parseInt(utcTime.slice(0, 2), 10);
  const utcMinutes = parseInt(utcTime.slice(2, 4), 10);

  if (utcHours > 23 || utcMinutes > 59) {
    return null;
  }

  // Parse offset (handle both "+0800" and "+08:00" formats - remove colon if present)
  const offsetSign = utcOffset[0] === '-' ? -1 : 1;
  const offsetString = utcOffset.replace(/[^\d]/g, '');

  if (offsetString.length !== 4) {
    return null; // Must have exactly 4 digits (hhmm)
  }

  const offsetHours = parseInt(offsetString.slice(0, 2), 10);
  const offsetMinutes = parseInt(offsetString.slice(2, 4), 10);

  // Calculate total minutes
  const utcTotalMinutes = utcHours * 60 + utcMinutes;
  const offsetTotalMinutes = (offsetHours * 60 + offsetMinutes) * offsetSign;
  let localTotalMinutes = utcTotalMinutes + offsetTotalMinutes;

  // Handle day changes
  let newDayChange = dayChange;
  while (localTotalMinutes < 0) {
    localTotalMinutes += 24 * 60;
    newDayChange -= 1;
  }
  while (localTotalMinutes >= 24 * 60) {
    localTotalMinutes -= 24 * 60;
    newDayChange += 1;
  }

  // Convert back to hours and minutes
  const localHours = Math.floor(localTotalMinutes / 60);
  const localMinutes = localTotalMinutes % 60;

  // Format as 4-digit string
  const localTime = `${String(localHours).padStart(2, '0')}${String(localMinutes).padStart(2, '0')}`;

  return { localTime, dayChange: newDayChange };
}

/**
 * Formats UTC offset to display format for helper text
 * @param offset - Offset in format "+0800" (backend format without colon)
 * @returns Formatted offset (e.g., "UTC+8" or "UTC+5:30")
 */
export function formatUtcOffset(offset: string): string {
  // Accept both "+0800" and "+08:00" formats
  if (!offset || !/^[+-]\d{2}:?\d{2}$/.test(offset)) {
    return '';
  }

  const sign = offset[0];
  const offsetString = offset.replace(/[^\d]/g, '');

  if (offsetString.length !== 4) {
    return '';
  }

  const hours = parseInt(offsetString.slice(0, 2), 10);
  const minutes = parseInt(offsetString.slice(2, 4), 10);

  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Gets day change indicator text
 * @param dayChange - Day change value (0, 1, -1, etc.)
 * @returns Human-readable day change text
 */
export function getDayChangeText(dayChange: number): string {
  if (dayChange === 0) return '';
  if (dayChange === 1) return ' (+1 day)';
  if (dayChange === -1) return ' (-1 day)';
  if (dayChange > 1) return ` (+${dayChange} days)`;
  return ` (${dayChange} days)`;
}

/**
 * Computes the local frequency pattern from a UTC frequency by shifting
 * each operating day based on the STD and UTC offset of the first station.
 *
 * When STD + UTC offset crosses midnight, the operating day shifts:
 *   - STD "2200" + offset "+0800" → local 0600 next day → day shifts +1
 *   - STD "0200" + offset "-0500" → local 2100 prev day → day shifts -1
 *
 * @param utcFrequency - IATA frequency in UTC (e.g., "7" for Sunday)
 * @param stdUTC - Departure time in UTC HHMM format (e.g., "2200")
 * @param utcOffset - UTC offset of departure station (e.g., "+0800")
 * @returns Local frequency pattern (e.g., "1" for Monday)
 *
 * @example
 * computeLocalFrequency("7", "2200", "+0800")   // "1"  (Sun UTC → Mon MYT)
 * computeLocalFrequency("1", "0200", "-0500")   // "7"  (Mon UTC → Sun local)
 * computeLocalFrequency("135", "1000", "+0800") // "135" (no day shift)
 */
export function computeLocalFrequency(
  utcFrequency: string,
  stdUTC: string,
  utcOffset: string,
): string {
  if (!utcFrequency || !stdUTC || !utcOffset) return utcFrequency || '';

  // Calculate the day shift from UTC to local
  const result = utcToLocalTime(stdUTC, utcOffset, 0);
  if (!result) return utcFrequency;

  const dayShift = result.dayChange; // 0, +1, or -1

  if (dayShift === 0) return utcFrequency; // No shift needed

  // Shift each IATA day (1=Mon ... 7=Sun) by dayShift
  const shiftedDays = utcFrequency
    .split('')
    .filter(d => d >= '1' && d <= '7')
    .map(d => {
      let shifted = parseInt(d, 10) + dayShift;
      // Wrap around: 0 → 7 (Sun), 8 → 1 (Mon)
      if (shifted < 1) shifted += 7;
      if (shifted > 7) shifted -= 7;
      return String(shifted);
    })
    .sort()
    .join('');

  return shiftedDays;
}

/**
 * Reverse of computeLocalFrequency: converts local frequency back to UTC.
 * Used when the user edits days in MYT mode — the toggle needs to be stored as UTC.
 */
export function computeUTCFrequency(
  localFrequency: string,
  stdUTC: string,
  utcOffset: string,
): string {
  if (!localFrequency || !stdUTC || !utcOffset) return localFrequency || '';

  const result = utcToLocalTime(stdUTC, utcOffset, 0);
  if (!result) return localFrequency;

  const dayShift = result.dayChange;
  if (dayShift === 0) return localFrequency;

  // Reverse the shift
  const shiftedDays = localFrequency
    .split('')
    .filter(d => d >= '1' && d <= '7')
    .map(d => {
      let shifted = parseInt(d, 10) - dayShift;
      if (shifted < 1) shifted += 7;
      if (shifted > 7) shifted -= 7;
      return String(shifted);
    })
    .sort()
    .join('');

  return shiftedDays;
}

/**
 * Returns the day shift (0 or +1) when converting STD from UTC to MYT (UTC+8).
 * Used to determine if calendar dates should shift when displaying in MYT.
 */
export function getMYTDayShift(stdUTC: string | undefined | null): number {
  if (!stdUTC) return 0;
  const result = utcToLocalTime(stdUTC, '+0800', 0);
  return result ? result.dayChange : 0;
}

/**
 * Shifts a YYYY-MM-DD date string by N days.
 * Used for UTC→MYT display (positive days) or MYT→UTC storage (negative days).
 */
export function shiftDateByDays(dateStr: string | undefined | null, days: number): string {
  if (!dateStr) return '';
  // Handle ISO strings like "2026-03-15T00:00:00.000Z" — take only the date part
  const datePart = dateStr.slice(0, 10);
  if (days === 0) return datePart;
  const [y, m, d] = datePart.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Reverse of shiftDateByDays: converts a local (MYT) date back to UTC.
 */
export function unshiftDateByDays(localDateStr: string, days: number): string {
  return shiftDateByDays(localDateStr, -days);
}

const MYT_OFFSET = '+0800';

/**
 * Converts a UTC time string to MYT (Malaysia Time, UTC+8)
 * @param utcTime - Time in 4-digit format (e.g., "0715")
 * @returns MYT time in 4-digit format, or original if conversion fails
 */
export function utcToMYT(utcTime: string): string {
  const result = utcToLocalTime(utcTime, MYT_OFFSET, 0);
  return result ? result.localTime : utcTime;
}

/**
 * Converts a MYT (Malaysia Time, UTC+8) time string to UTC
 * @param mytTime - Time in 4-digit format (e.g., "1515")
 * @returns UTC time in 4-digit format, or original if conversion fails
 */
export function mytToUTC(mytTime: string): string {
  // Subtracting 8 hours = adding -8 offset
  const result = utcToLocalTime(mytTime, '-0800', 0);
  return result ? result.localTime : mytTime;
}
